import { useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { navigation, type NavItem } from "@/config/navigation";
import { TenantSelector } from "./TenantSelector";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

function NavItemComponent({
  item,
  collapsed,
}: {
  item: NavItem;
  collapsed: boolean;
}) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    // Auto-expand if current path matches this section
    if (item.children) {
      return item.children.some((child) =>
        location.pathname.startsWith(child.href)
      );
    }
    return false;
  });

  const isActive = location.pathname === item.href;
  const isChildActive = item.children?.some(
    (child) => location.pathname === child.href
  );

  // Simple nav item without children
  if (!item.children) {
    return (
      <NavLink
        to={item.href}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground"
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span>{item.title}</span>}
      </NavLink>
    );
  }

  // Collapsible nav item with children
  if (collapsed) {
    // When collapsed, just show the parent icon
    return (
      <NavLink
        to={item.children?.[0]?.href ?? item.href}
        className={cn(
          "flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isChildActive
            ? "bg-accent text-accent-foreground"
            : "text-muted-foreground"
        )}
        title={item.title}
      >
        <item.icon className="h-4 w-4 shrink-0" />
      </NavLink>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            "flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            "hover:bg-accent hover:text-accent-foreground",
            isChildActive
              ? "bg-accent/50 text-accent-foreground"
              : "text-muted-foreground"
          )}
        >
          <span className="flex items-center gap-3">
            <item.icon className="h-4 w-4 shrink-0" />
            <span>{item.title}</span>
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-[accordion-up_0.2s_ease-out] data-[state=open]:animate-[accordion-down_0.2s_ease-out]">
        <div className="ml-4 mt-1 space-y-1 border-l pl-3">
          {item.children.map((child) => (
            <NavLink
              key={child.href}
              to={child.href}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                location.pathname === child.href
                  ? "bg-accent text-accent-foreground font-medium"
                  : "text-muted-foreground"
              )}
            >
              <span>{child.title}</span>
            </NavLink>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { canAccessRoute } = usePermissions();

  // Filter navigation items based on permissions
  const filteredNavigation = useMemo(() => {
    return navigation
      .map((item) => {
        // If item has children, filter children by permission
        if (item.children) {
          const accessibleChildren = item.children.filter((child) =>
            canAccessRoute(child.href)
          );
          // Only include parent if at least one child is accessible
          if (accessibleChildren.length === 0) {
            return null;
          }
          return { ...item, children: accessibleChildren };
        }
        // For items without children, check direct permission
        return canAccessRoute(item.href) ? item : null;
      })
      .filter((item): item is NavItem => item !== null);
  }, [canAccessRoute]);

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r bg-card transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo & Tenant Selector */}
      <div className="flex h-14 items-center border-b px-2">
        <TenantSelector collapsed={collapsed} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-2">
        {filteredNavigation.map((item) => (
          <NavItemComponent key={item.href} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="w-full justify-center"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>
    </aside>
  );
}
